import React, { useEffect, useState } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';

import './ViewportOverlay.css';
import { useSelector } from 'react-redux';
import Icons from '../Icons';

/**
 * Renders text overlays (top-left, top-right, bottom-left, bottom-right)
 * around the active viewport for metadata or status messages.
 *
 * The parent is responsible for styling offsets.
 */
const classes = {
  topLeft: 'overlay-top left-viewport',
  topRight: 'overlay-top right-viewport-scrollbar',
  bottomRight: 'overlay-bottom right-viewport-scrollbar',
  bottomLeft: 'overlay-bottom left-viewport',
};

function ViewportOverlay({ topLeft, topRight, bottomRight, bottomLeft, color = 'text-highlight', shadowClass = 'shadow-dark' }) {
  const overlay = 'absolute pointer-events-none viewport-overlay';
  const isPrint = useSelector(state => state.user.isPrint);

  // State to track visibility
  const [show, setShow] = useState(false);

  useEffect(() => {
    const stored = localStorage.getItem('show-overlay-items');
    setShow(stored === 'true'); // defaults to false if not explicitly true
  }, []);

  // Toggle handler
  const toggleOverlay = () => {
    const newShow = !show;
    setShow(newShow);
    localStorage.setItem('show-overlay-items', String(newShow));
  };

  if (isPrint) return null;

  const childrenArray = React.Children.toArray(topLeft?.props?.children);
  const firstChild = childrenArray[0];
  const remainingChildren = childrenArray.slice(1);
  return (
    <div className={classNames(color, 'overlay-text', shadowClass, 'text-base leading-5')}>
      <div
        data-cy={'viewport-overlay-toggle'}
        className={classNames(
          overlay,
          classes.topLeft,
          'pointer-events-auto -mt-6 flex cursor-pointer gap-x-2'
        )}
        onClick={toggleOverlay}
      >
        {window.innerWidth <= 768 ? (
          <div>
            <div>{firstChild || 'Toggle Overlay'}</div>{' '}
            <div className="flex items-center gap-x-1">
              {show ? 'Hide' : 'Show'}
              {show ? (
                <Icons.EyeHidden className="mt-0.5 h-2 w-2" />
              ) : (
                <Icons.EyeVisible className="mt-0.5 h-2 w-2" />
              )}
            </div>
          </div>
        ) : (
          <>
            {firstChild || 'Toggle Overlay'} {show ? 'Hide' : 'Show'}
            {show ? (
              <Icons.EyeHidden className="mt-0.5 h-4 w-4" />
            ) : (
              <Icons.EyeVisible className="mt-0.5 h-4 w-4" />
            )}
          </>
        )}
      </div>

      {show && (
        <div
          data-cy={'viewport-overlay-top-left'}
          className={classNames(overlay, classes.topLeft, 'mt-4 md:mt-0')}
        >
          {remainingChildren}
        </div>
      )}
      <div
        data-cy="viewport-overlay-top-right"
        className={classNames(overlay, classes.topRight)}
        style={{ transform: 'translateX(9px)' }}
      >
        {topRight}
      </div>
      <div
        data-cy="viewport-overlay-bottom-right"
        className={classNames(overlay, classes.bottomRight)}
        style={{ transform: 'translateX(6px)' }}
      >
        {bottomRight}
      </div>
      <div
        data-cy="viewport-overlay-bottom-left"
        className={classNames(overlay, classes.bottomLeft)}
      >
        {bottomLeft}
      </div>
    </div>
  );
}

ViewportOverlay.propTypes = {
  topLeft: PropTypes.node,
  topRight: PropTypes.node,
  bottomRight: PropTypes.node,
  bottomLeft: PropTypes.node,
  color: PropTypes.string,
  shadowClass: PropTypes.string,
};

export { ViewportOverlay };
